import {
  COMPANY_DOCUMENTS,
  chooseLibrary,
  DistributionScreen,
  DocumentLibraryScreen,
  type DocumentVessel,
  FolderTreeScreen,
  FormCategoriesScreen,
  LibraryPicker,
  type LibrarySection,
  recallLibrary,
  rememberLibrary,
  sectionHref,
  vesselLibrarySource,
} from '@felix/ui';
import { Center, Loader } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { Shell } from '../components/Shell.js';
import { officeDocumentSource } from '../documents-source.js';
import { useOfficeForms } from '../office-forms.js';
import { useSession } from '../session.js';

/** Kaynak oturumdaki kişiyle kurulur: basılı kopya altlığında basan kişi (ADR 0028 #30). */
export function useOfficeDocuments() {
  const session = useSession();
  const user = session.me?.user;
  const name = user?.fullName ?? user?.email ?? '';
  const owner = user?.id;
  // Altlıkta yer İngilizce "Office" (altlık İngilizcedir; ADR 0028 #30 A); yükleme devam kaydı
  // kişiye bağlı (inceleme M8a3-R22)
  return useMemo(
    () => officeDocumentSource({ name, place: 'Office', timeZone: null }, owner),
    [name, owner],
  );
}

/**
 * ISM Forms ve Manuals: breadcrumb satırındaki kütüphane seçiciyle şirket dokümanları ya da bir
 * geminin kütüphanesi (plan M8a4 Task 33; ADR 0038 §1, §12). Gemi kütüphanesi salt okunurdur.
 * Seçim adreste `?library=` olarak kalır ve kişiye göre hatırlanır; ilk açılışta ilk gemi.
 */
function LibraryPage({ section }: { section: LibrarySection }) {
  const { t } = useTranslation();
  const base = useOfficeDocuments();
  // Form List ▸ History sekmesinde formun kayıtları (ADR 0042 §21); ofiste kayıt açılmaz
  const forms = useOfficeForms();
  const userId = useSession().me?.user?.id;
  const location = useLocation();
  const navigate = useNavigate();
  const vesselLibrary = base.office?.vesselLibrary;
  const [vessels, setVessels] = useState<DocumentVessel[] | null>(vesselLibrary ? null : []);
  useEffect(() => {
    const office = base.office;
    if (!office?.vesselLibrary) return;
    let live = true;
    void office.vessels().then(
      (list) => live && setVessels(list),
      () => live && setVessels([]),
    );
    return () => {
      live = false;
    };
  }, [base]);
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const library =
    vessels === null
      ? null
      : vesselLibrary
        ? chooseLibrary(params, recallLibrary(userId), vessels)
        : COMPANY_DOCUMENTS;
  // Seçilen kütüphane adrese yazılır (yenileme ve paylaşılan bağlantı aynı kütüphaneyi açar)
  useEffect(() => {
    if (!library || !vesselLibrary || params.get('library') === library) return;
    const next = new URLSearchParams(params);
    next.set('library', library);
    navigate(`${location.pathname}?${next}`, { replace: true });
  }, [library, vesselLibrary, params, location.pathname, navigate]);
  const source = useMemo(
    () =>
      library && library !== COMPANY_DOCUMENTS && vesselLibrary
        ? vesselLibrarySource(base, library)
        : base,
    [base, library, vesselLibrary],
  );
  const choose = (value: string) => {
    rememberLibrary(userId, value);
    navigate(sectionHref(section, { library: value }));
  };
  return (
    <Shell title={t(`documents.sections.${section}.title`)}>
      {/* Ekran adreste kütüphane yazılınca çizilir: seçim (`?doc=`) kütüphaneyle birlikte kalır */}
      {library === null || (vesselLibrary && params.get('library') !== library) ? (
        <Center mih={240} aria-busy="true">
          <Loader size="sm" aria-label={t('documents.screen.loading')} />
        </Center>
      ) : (
        <DocumentLibraryScreen
          key={library}
          section={section}
          source={source}
          {...(section === 'ism' ? { forms } : {})}
          {...(vesselLibrary
            ? {
                librarySelector: (
                  <LibraryPicker value={library} vessels={vessels ?? []} onChange={choose} />
                ),
              }
            : {})}
        />
      )}
    </Shell>
  );
}

/** Compliance ▸ ISM Forms ▸ Form List (gemi-ism-formlari.jpeg; plan M8a Task 14). */
export function IsmFormsPage() {
  return <LibraryPage section="ism" />;
}

/** Compliance ▸ Manuals: aynı ekran, Manuals bölümü (plan M8a Task 18). */
export function ManualsPage() {
  return <LibraryPage section="manuals" />;
}

/**
 * Compliance ▸ ISM Forms ▸ Form Categories: gemi seçicili klasör ağacı yönetimi (plan M8a4 Task 16;
 * ADR 0038 §8). Seçili gemi adreste `?vessel=`.
 */
export function FormCategoriesPage() {
  const { t } = useTranslation();
  const source = useOfficeDocuments();
  return (
    <Shell title={t('documents.categories.title')}>
      <FormCategoriesScreen source={source} />
    </Shell>
  );
}

/**
 * Manuals ▸ "Manage folders" (`/compliance/manuals/folders`): menü öğesi değildir
 * (`menu-yapisi.md` değişmez); Manuals'ın breadcrumb satırındaki düğmeyle açılır (ADR 0038 §8).
 */
export function ManualsFoldersPage() {
  const { t } = useTranslation();
  const source = useOfficeDocuments();
  return (
    <Shell title={t('documents.folders.manualsTitle')}>
      <FolderTreeScreen section="manuals" source={source} />
    </Shell>
  );
}

/**
 * Dağıtım panosu (`/compliance/ism-forms/distribution`, `/compliance/manuals/distribution`):
 * menü öğesi değildir (ADR 0019); Form List ve Manuals'taki "Distribution" düğmesiyle açılır.
 */
export function DistributionPage({ section }: { section: LibrarySection }) {
  const { t } = useTranslation();
  const source = useOfficeDocuments();
  return (
    <Shell title={t('documents.distribution.title')}>
      <DistributionScreen section={section} source={source} />
    </Shell>
  );
}
